// @ts-nocheck
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { Page, LayoutSingleColumn, PrimaryButton, SecondaryButton } from '../components';
import TopbarContainer from '../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../containers/FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../ducks/ui.duck';
import { fetchAdminDisputedGigs, resolveAdminDispute } from '../util/api';

import css from './AdminDashboard.module.css';

const ADMIN_SECRET_STORAGE_KEY = 'betterexchange.arbitrationAdminSecret';

const resolveDefaultAdminSecret = () =>
  process.env.REACT_APP_ARBITRATION_ADMIN_SECRET ||
  process.env.REACT_APP_TELEMETRY_ADMIN_SECRET ||
  '';

export type DisputedGigRow = {
  notice_id: string;
  merchant_id: string;
  claimed_by_runner_id?: string | null;
  runner_payout_address?: string | null;
  pin_state?: string | null;
  escrow_status?: string | null;
  dispute_bias?: string | null;
  settlement_status?: string | null;
  title?: string | null;
  escrow_amount?: number | string | null;
  funding_status?: string | null;
};

const AdminDashboardComponent = ({ scrollingDisabled }) => {
  const [adminSecret, setAdminSecret] = useState(() => {
    if (typeof window === 'undefined') {
      return resolveDefaultAdminSecret();
    }
    return window.localStorage.getItem(ADMIN_SECRET_STORAGE_KEY) || resolveDefaultAdminSecret();
  });
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveError, setResolveError] = useState(null);
  const [lastResolvedId, setLastResolvedId] = useState(null);
  const [confirmResolve, setConfirmResolve] = useState(null);

  const loadDisputes = useCallback(async () => {
    if (!adminSecret) {
      setGigs([]);
      return;
    }

    setLoading(true);
    setLoadError(null);

    try {
      const response = await fetchAdminDisputedGigs(adminSecret);
      setGigs(Array.isArray(response.gigs) ? response.gigs : []);
    } catch (error) {
      setGigs([]);
      setLoadError(error instanceof Error ? error.message : 'Failed to load disputed gigs');
    } finally {
      setLoading(false);
    }
  }, [adminSecret]);

  useEffect(() => {
    loadDisputes();
    const intervalId = window.setInterval(loadDisputes, 5000);
    return () => window.clearInterval(intervalId);
  }, [loadDisputes]);

  const saveAdminSecret = event => {
    event.preventDefault();
    window.localStorage.setItem(ADMIN_SECRET_STORAGE_KEY, adminSecret);
    loadDisputes();
  };

  const handleResolve = async (noticeId, resolution) => {
    setResolvingId(noticeId);
    setResolveError(null);

    try {
      await resolveAdminDispute({ noticeId, resolution, adminSecret });
      setLastResolvedId(noticeId);
      setGigs(current => current.filter(gig => gig.notice_id !== noticeId));
    } catch (error) {
      setResolveError(error instanceof Error ? error.message : 'Resolution failed');
    } finally {
      setResolvingId(null);
      setConfirmResolve(null);
    }
  };

  const openConfirm = (noticeId, resolution) => {
    setConfirmResolve({ noticeId, resolution });
  };

  const confirmLabel =
    confirmResolve?.resolution === 'MERCHANT'
      ? 'Rule in favor of Merchant (Refund)'
      : 'Rule in favor of Runner (Force Payout)';

  return (
    <Page title="Arbitration Admin" scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        className={css.pageRoot}
      >
        <div className={css.container} data-testid="admin-dashboard-page">
          <header className={css.header}>
            <div>
              <h1 className={css.title}>Arbitration Admin</h1>
              <p className={css.subtitle}>
                Review disputed noticeboard gigs and route escrow to the winning party.
              </p>
            </div>
            <Link to="/" className={css.backLink}>
              Back to noticeboard
            </Link>
          </header>

          <section className={css.authCard}>
            <h2 className={css.sectionTitle}>Admin access</h2>
            <form className={css.authForm} onSubmit={saveAdminSecret}>
              <label className={css.field}>
                Bearer secret
                <input
                  type="password"
                  value={adminSecret}
                  onChange={event => setAdminSecret(event.target.value)}
                  placeholder="TELEMETRY_ADMIN_SECRET"
                  data-testid="admin-secret-input"
                />
              </label>
              <SecondaryButton type="submit">Save & reload disputes</SecondaryButton>
            </form>
          </section>

          <section className={css.listCard}>
            <h2 className={css.sectionTitle}>Active disputes</h2>
            {loadError ? <p className={css.error}>{loadError}</p> : null}
            {resolveError ? <p className={css.error}>{resolveError}</p> : null}
            {lastResolvedId ? (
              <p className={css.success} data-testid="admin-resolve-success">
                Dispute {lastResolvedId.slice(0, 8)}… resolved on-chain.
              </p>
            ) : null}
            {loading && gigs.length === 0 ? <p>Loading disputes…</p> : null}
            {!loading && gigs.length === 0 ? (
              <p className={css.empty} data-testid="admin-no-disputes">
                No gigs in DISPUTED state.
              </p>
            ) : (
              <div className={css.tableWrap}>
                <table className={css.disputeTable}>
                  <thead>
                    <tr>
                      <th>Gig</th>
                      <th>Notice ID</th>
                      <th>Merchant wallet</th>
                      <th>Runner wallet</th>
                      <th>Escrow</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gigs.map(gig => (
                      <tr
                        key={gig.notice_id}
                        data-testid={`admin-dispute-item-${gig.notice_id}`}
                      >
                        <td className={css.titleCell}>{gig.title || 'Untitled gig'}</td>
                        <td className={css.walletCell}>{gig.notice_id}</td>
                        <td className={css.walletCell}>{gig.merchant_id}</td>
                        <td className={css.walletCell}>
                          {gig.runner_payout_address ||
                            gig.claimed_by_runner_id ||
                            '—'}
                        </td>
                        <td className={css.amountCell}>{gig.escrow_amount} ETH</td>
                        <td className={css.actionsCell}>
                          <div className={css.actions}>
                            <PrimaryButton
                              type="button"
                              className={css.ruleMerchant}
                              inProgress={resolvingId === gig.notice_id}
                              disabled={Boolean(resolvingId)}
                              onClick={() => openConfirm(gig.notice_id, 'MERCHANT')}
                              data-testid="admin-rule-merchant"
                            >
                              Rule in favor of Merchant (Refund)
                            </PrimaryButton>
                            <SecondaryButton
                              type="button"
                              className={css.ruleRunner}
                              inProgress={resolvingId === gig.notice_id}
                              disabled={Boolean(resolvingId) || !gig.claimed_by_runner_id}
                              onClick={() => openConfirm(gig.notice_id, 'RUNNER')}
                              data-testid="admin-rule-runner"
                            >
                              Rule in favor of Runner (Force Payout)
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {confirmResolve ? (
          <div className={css.modalOverlay} role="dialog" aria-modal="true">
            <div className={css.modal}>
              <h3 className={css.modalTitle}>Confirm permanent on-chain ruling</h3>
              <p className={css.modalBody}>
                This action broadcasts <strong>{confirmLabel}</strong> to the Hub escrow contract.
                It cannot be undone. Proceed only after reviewing dispute evidence.
              </p>
              <div className={css.modalActions}>
                <PrimaryButton
                  type="button"
                  className={css.modalConfirmDanger}
                  inProgress={resolvingId === confirmResolve.noticeId}
                  disabled={Boolean(resolvingId)}
                  onClick={() =>
                    handleResolve(confirmResolve.noticeId, confirmResolve.resolution)
                  }
                  data-testid="admin-rule-confirm"
                >
                  Confirm ruling
                </PrimaryButton>
                <SecondaryButton
                  type="button"
                  disabled={Boolean(resolvingId)}
                  onClick={() => setConfirmResolve(null)}
                  data-testid="admin-rule-cancel"
                >
                  Cancel
                </SecondaryButton>
              </div>
            </div>
          </div>
        ) : null}
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const AdminDashboard = compose(connect(mapStateToProps))(AdminDashboardComponent);

export default AdminDashboard;
